import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import UserService from '../../services/userService';
import AddUserModal from '../modals/AddUserModal';
import './DashboardPages.css';

const UserManagementPage = () => {
  const { userType } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 6;

  // Format last login date
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    try {
      const date = new Date(lastLogin);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Never';
    }
  };

  // Fetch users function
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await UserService.getUsers();
      if (result.success) {
        setUsers(result.data);
      } else {
        showError(result.error || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError('An error occurred while loading users');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Pagination calculations
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate role statistics from users data
  const roleStats = {
    superAdmins: users.filter(u => u.user_type === 'SUPER_ADMIN').length,
    admins: users.filter(u => u.user_type === 'ADMIN').length,
    staff: users.filter(u => u.user_type === 'STAFF').length
  };

  // Get last 2 users added (most recent first)
  const recentUsers = [...users]
    .sort((a, b) => {
      const dateA = new Date(a.created_at || a.createdAt || 0);
      const dateB = new Date(b.created_at || b.createdAt || 0);
      return dateB - dateA;
    })
    .slice(0, 2);

  // Helper to get user display name
  const getUserDisplayName = (user) => {
    return user.firstname && user.lastname 
      ? `${user.firstname} ${user.lastname}`
      : user.firstname || user.full_name || user.name || user.email || 'Unknown User';
  };

  // Helper to format time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  // Check if user has access (admin only)
  if (userType !== 'SUPER_ADMIN') {
    return (
      <div className="dashboard-page">
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You don't have permission to access the User Management panel.</p>
          <p>This feature is available to Admin users only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <p className="page-description">
          Manage user accounts, roles, and permissions for the Venturi IMS system.
        </p>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2 className="card-title">User Directory</h2>
            <button className="btn btn-primary" onClick={() => setIsAddUserModalOpen(true)}>Add New User</button>
          </div>
          <div className="card-content">
            <div className="user-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="loading-message">Loading users...</td>
                    </tr>
                  ) : currentUsers && currentUsers.length > 0 ? (
                    currentUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">{user.firstname ? user.firstname.charAt(0).toUpperCase() : (user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U')}</div>
                            <span>{user.firstname || user.full_name || user.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td><span className={`role-badge ${(user.user_type || user.role || 'staff').toLowerCase()}`}>{user.user_type || user.role || 'Staff'}</span></td>
                        <td><span className={`status-badge ${user.is_active === false ? 'inactive' : 'active'}`}>{user.is_active === false ? 'Inactive' : 'Active'}</span></td>
                        <td>{formatLastLogin(user.last_login || user.lastLogin)}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon" title="Edit user">
                              <svg viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button className="btn-icon danger" title="Delete user">
                              <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L9 12.414l-2.293 2.293a1 1 0 101.414 1.414L11.414 13l2.293 2.293a1 1 0 001.414-1.414L12.414 11l2.293-2.293z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data-message">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {!isLoading && users.length > usersPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, users.length)} of {users.length} users
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                  </button>
                  
                  <div className="pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Role Statistics</h2>
          </div>
          <div className="card-content">
            <div className="role-stats">
              <div className="stat-item">
                <span className="stat-number">{roleStats.superAdmins}</span>
                <span className="stat-label">Super Admins</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{roleStats.admins}</span>
                <span className="stat-label">Admins</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{roleStats.staff}</span>
                <span className="stat-label">Staff</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Recent Activity</h2>
          </div>
          <div className="card-content">
            <div className="activity-list">
              {recentUsers.length > 0 ? (
                recentUsers.map((user, index) => (
                  <div key={user.id || index} className="activity-item">
                    <div className="activity-icon">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6z" />
                      </svg>
                    </div>
                    <div className="activity-content">
                      <p>New user "{getUserDisplayName(user)}" added</p>
                      <span className="activity-time">{getTimeAgo(user.created_at || user.createdAt)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-item">
                  <div className="activity-content">
                    <p>No recent activity</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onUserCreated={async () => {
          await fetchUsers();
          setCurrentPage(1);
          showSuccess('User created successfully');
        }}
      />
    </div>
  );
};

export default UserManagementPage;